import React from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { PaymentReceiveModal } from 'sectors/pilates/members/components/paymentReceive/PaymentReceiveModal';
import IncomeReceivablesCard from './IncomeReceivablesCard';
import IncomePaymentsCard from './IncomePaymentsCard';
import { trMoney } from '../../utils/accountingFormat';
import { SessionDebtPayDialog } from 'sectors/pilates/payments/modal/tabs/SessionPaymentTab';
import { SmsSendModal } from 'sectors/pilates/members/components/MemberActionsMenu/SmsSendModal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { MemberDetailModal } from 'sectors/pilates/members/modal/MemberDetailModal/MemberDetailModal';
import { PaymentDetailModal } from 'sectors/pilates/members/modal/MemberDetailModal/tabs/components/PaymentsTab/PaymentDetailModal';
import { PaymentEditModal } from 'sectors/pilates/members/components/paymentReceive/PaymentEditModal';
import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { smsApi } from 'sectors/pilates/sms/api/smsApi';
function SummaryCard({ title, value, surface, border }) {
  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: `1px solid ${border}`,
        background: surface,
        boxShadow: 'none',
      }}
    >
      <MDBox sx={{ p: 1.2 }}>
        <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800 }}>
          {title}
        </MDTypography>
        <MDTypography variant="h6" fontWeight="bold">
          {value}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

function mapIncomeSessionRowToPaymentDialogRow(row) {
  return {
    sessionId: row.sessionId ?? null,
    dayISO: row.dayISO ?? null,
    startMin: row.startMin ?? null,
    durationMin: row.durationMin ?? null,
    activityName: row.activityName ?? null,
    trainerId: row.trainerId ?? null,
    trainerName: row.trainerName ?? null,

    sessionParticipantId: row.sessionParticipantId ?? row.sessionId ?? null,
    memberId: row.memberId ?? null,
    fullName: row.fullName ?? null,
    phone: row.phone ?? null,

    totalTry: row.amountTry ?? 0,
    paidTry: 0,
    sessionDebtTry: Number(row.amountTry || 0),
    paymentStatus: 'PENDING',
  };
}

function mapCollectedPaymentToModal(row) {
  return {
    id: row.paymentId,
    fullName: row.fullName,
    payerName: row.payerName,
    amountTry: row.amountTry,
    paidDate: row.paidDate,
    method: row.method,
    note: row.note,
    purpose: row.paymentType,
    receivedByName: row.receivedByName,
    approvedByName: row.approvedByName,
    approvedAt: row.approvedAt,
    createdAt: row.createdAt,
    planNameSnapshot: row.packageName,
    planStatus: row.planStatus,
  };
}

export default function AccountingIncomeTab({
  surface,
  surface2,
  border,
  q,
  setQ,
  onlyOverdue,
  setOnlyOverdue,
  data,
}) {
  const [selectedReceivable, setSelectedReceivable] = React.useState(null);
  const [sessionPayOpen, setSessionPayOpen] = React.useState(false);
  const [smsOpen, setSmsOpen] = React.useState(false);
  const [smsMember, setSmsMember] = React.useState(null);
  const [smsDefaultMessage, setSmsDefaultMessage] = React.useState('');
  const [packagePayOpen, setPackagePayOpen] = React.useState(false);
  const [packagePayMember, setPackagePayMember] = React.useState(null);

  const handleReceivePayment = React.useCallback((row) => {
    if (!row) return;

    if (row.paymentType === 'SESSION') {
      setSelectedReceivable(row);
      setSessionPayOpen(true);
      return;
    }

    if (row.paymentType === 'PACKAGE') {
      setPackagePayMember({
        id: row.memberId,
        fullName: row.fullName,
        phone: row.phone,
        memberPlanId: row.memberPlanId,
      });
      setPackagePayOpen(true);
    }
  }, []);
  const handleSendReminder = React.useCallback((row) => {
    if (!row) return;
    function getFirstName(fullName) {
      if (!fullName) return '';
      const parts = fullName.trim().split(' ');
      return parts[0] || '';
    }
    const member = {
      id: row.memberId ?? null,
      fullName: row.fullName || row.payerName || 'Üye',
      phone: row.phone || row.payerPhone || '',
    };

    let message = '';

    const name = getFirstName(member.fullName);

    if (row.paymentType === 'SESSION') {
      const activity = row.activityName || 'Seans';
      const day = row.dayISO || '';

      message =
        `${name}, ` +
        `${activity} seansınıza ait ${trMoney(
          row.amountTry || 0
        )} tutarında bekleyen ödeme bulunmaktadır.` +
        `${day ? ` (Tarih: ${day})` : ''}. ` +
        `İyi Günler Dileriz. `;
    } else if (row.paymentType === 'PACKAGE') {
      message =
        `Sayın ${name}, ` +
        ` ${trMoney(row.amountTry || 0)} tutarında bekleyen ödemeniz bulunmaktadır. ` +
        `İyi Günler Dileriz.`;
    } else {
      message =
        `Sayın ${name}, ` +
        `hesabınızda ${trMoney(row.amountTry || 0)} tutarında bekleyen ödeme bulunmaktadır. ` +
        `İyi Günler Dileriz.`;
    }

    setSmsMember(member);
    setSmsDefaultMessage(message);
    setSmsOpen(true);
  }, []);
  const handleSessionPaid = React.useCallback(() => {
    setSessionPayOpen(false);
    setSelectedReceivable(null);

    if (typeof data?.refetch === 'function') {
      data.refetch();
    }
  }, [data]);

  const handlePackagePaid = React.useCallback(() => {
    setPackagePayOpen(false);
    setPackagePayMember(null);

    if (typeof data?.refetch === 'function') {
      data.refetch();
    }
  }, [data]);
  const [memberDetailOpen, setMemberDetailOpen] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState(null);

  const [infoOpen, setInfoOpen] = React.useState(false);
  const [infoMessage, setInfoMessage] = React.useState('');
  const [selectedPayment, setSelectedPayment] = React.useState(null);
  const [paymentDetailOpen, setPaymentDetailOpen] = React.useState(false);
  const [paymentEditOpen, setPaymentEditOpen] = React.useState(false);
  const handleOpenMember = React.useCallback((row) => {
    if (!row?.memberId) {
      setInfoMessage('Kayıtlı üye değil.');
      setInfoOpen(true);
      return;
    }

    const fullName = row.fullName || '';
    const parts = fullName.trim().split(' ');
    const name = parts[0] || '';
    const surname = parts.slice(1).join(' ') || '';

    setSelectedMember({
      id: row.memberId,
      fullName: row.fullName || '',
      name,
      surname,
      phone: row.phone || '',
    });
    setMemberDetailOpen(true);
  }, []);
  return (
    <>
      <MDBox sx={{ display: 'grid', gap: 1.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={6}>
            <SummaryCard
              title="Toplam Alınan Ödeme"
              value={trMoney(data.totalReceived)}
              surface={surface}
              border={border}
            />
          </Grid>
          <Grid item xs={6} md={6}>
            <SummaryCard
              title="Bekleyen Gelir"
              value={trMoney(data.totalPendingIncome)}
              surface={surface}
              border={border}
            />
          </Grid>
        </Grid>

        <MDBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ara: üye / telefon / eğitmen / not"
            sx={{
              minWidth: 320,
              maxWidth: 420,
              '& .MuiOutlinedInput-root': {
                borderRadius: '14px',
                background: surface2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon fontSize="small">search</Icon>
                </InputAdornment>
              ),
            }}
          />
        </MDBox>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <IncomeReceivablesCard
              rows={data.filteredCustomers}
              onReceivePayment={handleReceivePayment}
              onSendReminder={handleSendReminder}
              onOpenMember={handleOpenMember}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <IncomePaymentsCard
              rows={data.filteredPayments}
              onOpenMember={handleOpenMember}
              onViewPayment={(row) => {
                setSelectedPayment(mapCollectedPaymentToModal(row));
                setPaymentDetailOpen(true);
              }}
              onEditPayment={(row) => {
                setSelectedPayment(mapCollectedPaymentToModal(row));
                setPaymentEditOpen(true);
              }}
            />
          </Grid>
        </Grid>
      </MDBox>

      <SessionDebtPayDialog
        open={sessionPayOpen}
        onClose={() => {
          setSessionPayOpen(false);
          setSelectedReceivable(null);
        }}
        row={
          selectedReceivable?.paymentType === 'SESSION'
            ? mapIncomeSessionRowToPaymentDialogRow(selectedReceivable)
            : null
        }
        onPaid={handleSessionPaid}
      />

      <PaymentReceiveModal
        open={packagePayOpen}
        member={packagePayMember}
        initialStep="PLAN"
        onClose={() => {
          setPackagePayOpen(false);
          setPackagePayMember(null);
        }}
        onSuccess={handlePackagePaid}
      />
      <SmsSendModal
        open={smsOpen}
        onClose={() => {
          setSmsOpen(false);
          setSmsMember(null);
          setSmsDefaultMessage('');
        }}
        member={smsMember}
        companyName="Solisup"
        defaultMessage={smsDefaultMessage}
        onSend={async ({ memberId, phone, message }) => {
          const payload = {
            memberId: memberId ?? null,
            memberName: smsMember?.fullName || null,
            message,
            phone,
          };

          const res = await smsApi.sendManual(payload);
          if (res?.ok === false) {
            const err =
              res?.error ||
              (Array.isArray(res?.errors) ? res.errors[0] : null) ||
              res?.message ||
              'SMS gonderilemedi.';
            throw new Error(err);
          }

          return res;
        }}
      />
      <MemberDetailModal
        open={memberDetailOpen}
        member={selectedMember}
        onClose={() => {
          setMemberDetailOpen(false);
          setSelectedMember(null);
        }}
      />
      <Snackbar
        open={infoOpen}
        autoHideDuration={2500}
        onClose={() => setInfoOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setInfoOpen(false)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {infoMessage}
        </Alert>
      </Snackbar>

      <PaymentDetailModal
        open={paymentDetailOpen}
        payment={selectedPayment}
        onClose={() => {
          setPaymentDetailOpen(false);
          setSelectedPayment(null);
        }}
      />

      <PaymentEditModal
        open={paymentEditOpen}
        payment={selectedPayment}
        memberLabel={selectedPayment?.fullName || selectedPayment?.payerName || 'Odeme'}
        onClose={() => {
          setPaymentEditOpen(false);
          setSelectedPayment(null);
        }}
        onSave={async (paymentId, patch) => {
          await membersApi.updatePayment(paymentId, patch);
          await data?.refetch?.();
        }}
      />
    </>
  );
}
